import api from './api';

const STORAGE_KEY = 'rfpTemplates';
let useLocalOnly = false;

const TENDER_SEED_TEMPLATES = [
  {
    _id: 'seed_tender_eoi_master',
    templatePurpose: 'RFP_EOI',
    templateName: 'MMRDA Metro Loan EOI Master Template',
    projectType: 'Infrastructure Finance',
    content: `
<h1>NOTICE INVITING EXPRESSION OF INTEREST (EOI)</h1>
<p>{{authority_name}}</p>
<p>{{address}}</p>

<h2>SECTION - I: Introduction</h2>
<p>{{introduction}}</p>

<h2>EOI Process Schedule</h2>
<table>
  <tr><th>Sr No</th><th>Particulars</th><th>Timelines</th></tr>
  {{#each eoi_schedule}}
  <tr>
    <td>{{sr_no}}</td>
    <td>{{particular}}</td>
    <td>{{timeline}}</td>
  </tr>
  {{/each}}
</table>

<h2>SECTION - III: Project Background</h2>
<p>{{project_background}}</p>

<h2>SECTION - IV: Qualifying Requirements</h2>
<ul>
  {{#each eligibility_points}}
  <li>{{this}}</li>
  {{/each}}
</ul>

<h2>SECTION - V: Detailed Term Sheet</h2>
<table>
  <tr><th>Particular</th><th>Details</th></tr>
  {{#each term_sheet}}
  <tr>
    <td>{{label}}</td>
    <td>{{value}}</td>
  </tr>
  {{/each}}
</table>

<h2>SECTION - VI: Financial Bid Format</h2>
<p>{{financial_bid_details}}</p>
`.trim(),
    sectionMapping: [
      'introduction',
      'project_background',
      'eligibility_points',
      'term_sheet',
      'financial_bid_details',
      'eoi_schedule',
    ],
    isActive: true,
  },
  {
    _id: 'seed_tender_term_sheet',
    templatePurpose: 'RFP_EOI',
    templateName: 'MMRDA Detailed Term Sheet Template',
    projectType: 'Infrastructure Finance',
    content: `
<h2>DETAILED TERM SHEET</h2>
<table>
  <tr><th>Sr No</th><th>Particular</th><th>Details</th></tr>
  {{#each term_sheet_rows}}
  <tr>
    <td>{{sr_no}}</td>
    <td>{{particular}}</td>
    <td>{{detail}}</td>
  </tr>
  {{/each}}
</table>

<h3>Loan Structure</h3>
<p>{{loan_structure}}</p>

<h3>Interest Rate</h3>
<p>{{interest_rate_terms}}</p>

<h3>Security and Covenants</h3>
<p>{{security_structure}}</p>
<p>{{borrower_covenants}}</p>
`.trim(),
    sectionMapping: [
      'term_sheet_rows',
      'loan_structure',
      'interest_rate_terms',
      'security_structure',
      'borrower_covenants',
    ],
    isActive: false,
  },
  {
    _id: 'seed_tender_financial_bid',
    templatePurpose: 'RFP_EOI',
    templateName: 'MMRDA Financial Bid Format Template',
    projectType: 'Infrastructure Finance',
    content: `
<h2>FINANCIAL BID FORMAT</h2>
<p>Bidder Name: {{bidder_name}}</p>
<p>Address: {{bidder_address}}</p>
<p>Bid Submission Date: {{bid_submission_date}}</p>

<table>
  <tr><th>Project</th><th>Project Cost (Rs Cr)</th><th>Loan Requirement (Rs Cr)</th><th>Loan Offered (Rs Cr)</th><th>Rate of Interest</th></tr>
  {{#each financial_bid_projects}}
  <tr>
    <td>{{project_name}}</td>
    <td>{{project_cost}}</td>
    <td>{{loan_requirement}}</td>
    <td>{{loan_offered}}</td>
    <td>{{interest_rate}}</td>
  </tr>
  {{/each}}
</table>

<h3>Interest Components</h3>
<p>Benchmark Type: {{benchmark_type}}</p>
<p>Benchmark Rate: {{benchmark_rate}}</p>
<p>Spread: {{spread_rate}}</p>
<p>Effective Rate: {{effective_rate}}</p>
`.trim(),
    sectionMapping: [
      'bidder_name',
      'bidder_address',
      'bid_submission_date',
      'financial_bid_projects',
      'benchmark_type',
      'benchmark_rate',
      'spread_rate',
      'effective_rate',
    ],
    isActive: false,
  },
];

const normalizeTemplate = (tpl = {}) => ({
  _id: tpl._id || String(Date.now() + Math.random()),
  templatePurpose: tpl.templatePurpose || 'RFP_EOI',
  templateName: tpl.templateName || '',
  projectType: tpl.projectType || 'General',
  content: tpl.content || '',
  placeholders:
    Array.isArray(tpl.placeholders) && tpl.placeholders.length
      ? tpl.placeholders
      : extractPlaceholders(tpl.content || ''),
  sectionMapping: Array.isArray(tpl.sectionMapping) ? tpl.sectionMapping : [],
  isActive: Boolean(tpl.isActive),
  createdAt: tpl.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const getLocalTemplates = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const setLocalTemplates = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const ensureTenderSeedTemplates = () => {
  const existing = getLocalTemplates();
  const existingNormalized = existing.map(normalizeTemplate);

  const existingKeys = new Set(
    existingNormalized.map(
      (tpl) => `${tpl.templatePurpose}::${tpl.templateName}`,
    ),
  );

  const missingSeeds = TENDER_SEED_TEMPLATES.filter(
    (tpl) =>
      !existingKeys.has(
        `${tpl.templatePurpose || 'RFP_EOI'}::${tpl.templateName}`,
      ),
  ).map((tpl) =>
    normalizeTemplate({
      ...tpl,
      placeholders: extractPlaceholders(tpl.content),
    }),
  );

  if (missingSeeds.length === 0) {
    return existingNormalized;
  }

  const merged = [...missingSeeds, ...existingNormalized];
  setLocalTemplates(merged);
  return merged;
};

const extractPlaceholders = (content = '') => {
  const regex = /\{\{([^}]+)\}\}/g;
  const values = new Set();
  let match;

  while ((match = regex.exec(content)) !== null) {
    const name = String(match[1] || '').trim();
    if (name) values.add(name);
  }

  return Array.from(values);
};

class RfpTemplateService {
  static extractPlaceholders(content = '') {
    return extractPlaceholders(content);
  }

  static async getTemplates() {
    if (useLocalOnly) {
      return { data: ensureTenderSeedTemplates() };
    }

    try {
      const response = await api.get('/sourcing/rfp-templates');
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      return { data: list.map(normalizeTemplate) };
    } catch (error) {
      if (error?.response?.status === 404) useLocalOnly = true;
      return { data: ensureTenderSeedTemplates() };
    }
  }

  static async createTemplate(payload) {
    const template = normalizeTemplate(payload);
    if (useLocalOnly) {
      const current = getLocalTemplates().map(normalizeTemplate);
      const next = [template, ...current];
      setLocalTemplates(next);
      return { success: true, data: template };
    }

    try {
      const response = await api.post('/sourcing/rfp-templates', template);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) useLocalOnly = true;
      const current = getLocalTemplates().map(normalizeTemplate);
      const next = [template, ...current];
      setLocalTemplates(next);
      return { success: true, data: template };
    }
  }

  static async updateTemplate(id, payload) {
    const updated = normalizeTemplate({ ...payload, _id: id });
    if (useLocalOnly) {
      const current = getLocalTemplates().map(normalizeTemplate);
      const next = current.map((tpl) => (tpl._id === id ? updated : tpl));
      setLocalTemplates(next);
      return { success: true, data: updated };
    }

    try {
      const response = await api.put(`/sourcing/rfp-templates/${id}`, updated);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) useLocalOnly = true;
      const current = getLocalTemplates().map(normalizeTemplate);
      const next = current.map((tpl) => (tpl._id === id ? updated : tpl));
      setLocalTemplates(next);
      return { success: true, data: updated };
    }
  }

  static async deleteTemplate(id) {
    if (useLocalOnly) {
      const current = getLocalTemplates().map(normalizeTemplate);
      setLocalTemplates(current.filter((tpl) => tpl._id !== id));
      return { success: true };
    }

    try {
      const response = await api.delete(`/sourcing/rfp-templates/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) useLocalOnly = true;
      const current = getLocalTemplates().map(normalizeTemplate);
      setLocalTemplates(current.filter((tpl) => tpl._id !== id));
      return { success: true };
    }
  }

  static async cloneTemplate(id) {
    const listRes = await this.getTemplates();
    const source = (listRes.data || []).find((tpl) => tpl._id === id);
    if (!source) {
      throw new Error('Template not found');
    }

    const clonePayload = {
      ...source,
      _id: undefined,
      templateName: `${source.templateName} (Clone)`,
      isActive: false,
    };

    return this.createTemplate(clonePayload);
  }

  static async activateTemplate(id) {
    const listRes = await this.getTemplates();
    const list = listRes.data || [];
    const updates = list.map((tpl) => ({ ...tpl, isActive: tpl._id === id }));

    if (useLocalOnly) {
      setLocalTemplates(updates.map(normalizeTemplate));
      return { success: true };
    }

    try {
      await Promise.all(
        updates.map((tpl) =>
          api.put(`/sourcing/rfp-templates/${tpl._id}`, normalizeTemplate(tpl)),
        ),
      );
    } catch (error) {
      if (error?.response?.status === 404) useLocalOnly = true;
      setLocalTemplates(updates.map(normalizeTemplate));
    }

    return { success: true };
  }
}

export default RfpTemplateService;
